import { Injectable, computed, signal } from '@angular/core';
import { BehaviorSubject, map } from 'rxjs';
import { ResourcesService } from '../../user/resources.service';
import {
  CombinedProjectOverviewResponse,
  ProjectIfs,
} from '../delivery-overview.entities';

@Injectable({
  providedIn: 'root',
})
export class DeliveryOverviewService {
  filterString = signal<string>('');

  projects = signal<CombinedProjectOverviewResponse[]>([]);
  hasNoProjectMatches = computed(() => {
    const filter = this.filterString().trim().toLowerCase();

    // Do not show "Create IFS" for an empty search field.
    if (!filter) {
      return false;
    }

    return !this.projects().some((project) => {
      return (
        project.value?.toLowerCase().includes(filter) ||
        project.name?.toLowerCase().includes(filter)
      );
    });
  });

  starredProjects = signal<ProjectIfs[]>(this.localStorageStarredProjects);

  projects$ = new BehaviorSubject<CombinedProjectOverviewResponse[]>([]);
  starredProjects$ = new BehaviorSubject<CombinedProjectOverviewResponse[]>([]);

  constructor(private resourcesService: ResourcesService) {}

  get localStorageStarredProjects(): ProjectIfs[] {
    const storedArrayString = localStorage.getItem(
      'starredDeliveryOverviewProjects',
    );

    const storedArray =
      (storedArrayString && JSON.parse(storedArrayString)) || [];

    return storedArray as ProjectIfs[];
  }

  getInitialProjects() {
    return this.resourcesService.getEntities$().pipe(
      map((projects) => {
        const newList = projects.map((project) => ({
          name: project.name,
          id: project.id,
          value: project.value,
          starred: this.localStorageStarredProjects
            ?.map(({ value }) => value)
            .includes(project.value),
          completedResourceResponse: project.completedResourceResponse,
        }));

        this.projects.set(newList);
        this.projects$.next(newList);
        this.starredProjects$.next(newList.filter(({ starred }) => starred));

        return newList;
      }),
    );
  }

  getProjects$() {
    return this.projects$.asObservable();
  }

  setProjects(projects: CombinedProjectOverviewResponse[]) {
    this.projects.set(projects);
    this.projects$.next(projects);
  }

  getStarredProjects$() {
    return this.starredProjects$.asObservable();
  }

  setStarredProjects(projects: CombinedProjectOverviewResponse[]) {
    this.starredProjects$.next(projects);
  }

  setFilterString(filter: string) {
    this.filterString.set(filter);
  }

  updateProject(updatedProject: CombinedProjectOverviewResponse) {
    const newProjects = this.projects$.getValue().map((project) => {
      if (project.value === updatedProject.value) {
        return {
          ...project,
          starred: updatedProject.starred,
        };
      }

      return project;
    });

    this.projects.set(newProjects);
    this.projects$.next(newProjects);
  }

  updateStarredProjectsList(project: CombinedProjectOverviewResponse) {
    if (project.starred === true) {
      this.starredProjects$.next(
        this.starredProjects$.getValue()
          ? [...this.starredProjects$.getValue(), project]
          : [project],
      );

      return;
    }

    this.starredProjects$.next(
      this.starredProjects$
        .getValue()
        .filter(({ value }) => value !== project.value),
    );
  }

  onUpdateStarredProject(project: CombinedProjectOverviewResponse) {
    if (project.starred) {
      this.removeStarredProject(project);
      return;
    }

    this.addStarredProject(project);
  }

  addStarredProject(project: CombinedProjectOverviewResponse) {
    const updatedProject = {
      ...project,
      starred: true,
    };

    this.starredProjects.update((currentProjects) =>
      currentProjects
        ? [...currentProjects, { value: updatedProject.value }]
        : [{ value: updatedProject.value }],
    );

    this.setLocalStorageStarredProjects();
    this.updateProject(updatedProject);
    this.updateStarredProjectsList(updatedProject);
  }

  removeStarredProject(project: CombinedProjectOverviewResponse) {
    const updatedProject = {
      ...project,
      starred: false,
    };

    this.starredProjects.update((currentProjects) =>
      currentProjects.filter(({ value }) => value !== updatedProject.value),
    );

    this.setLocalStorageStarredProjects();
    this.updateProject(updatedProject);
    this.updateStarredProjectsList(updatedProject);
  }

  setLocalStorageStarredProjects() {
    localStorage.setItem(
      'starredDeliveryOverviewProjects',
      JSON.stringify(this.starredProjects()),
    );
  }
}
